import { useState } from 'react';
import { TaxCalculation } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ResultsTableProps {
  calculations: TaxCalculation[];
  onProductClick: (calculation: TaxCalculation) => void;
  onSort: (field: keyof TaxCalculation) => void;
  sortField: keyof TaxCalculation;
  sortDirection: 'asc' | 'desc';
}

const ResultsTable = ({
  calculations,
  onProductClick,
  onSort,
  sortField,
  sortDirection
}: ResultsTableProps) => {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-success/10 text-success';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'error':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'Conforme';
      case 'warning':
        return 'Atenção';
      case 'error':
        return 'Erro';
      default:
        return 'Desconhecido';
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(calculations.map(c => c.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  const SortIcon = ({ field }: { field: keyof TaxCalculation }) => {
    if (sortField !== field) {
      return <Icon name="ChevronsUpDown" size={16} color="var(--color-muted-foreground)" />;
    }
    return (
      <Icon
        name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
        size={16}
        color="var(--color-primary)"
      />
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows.size === calculations.length && calculations.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                  aria-label="Selecionar todos"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('productCode')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Código
                  <SortIcon field="productCode" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('productName')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                >
                  Produto
                  <SortIcon field="productName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-semibold text-foreground">NCM</span>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('icms')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors ml-auto"
                >
                  ICMS
                  <SortIcon field="icms" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('ipi')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors ml-auto"
                >
                  IPI
                  <SortIcon field="ipi" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('pis')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors ml-auto"
                >
                  PIS
                  <SortIcon field="pis" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('cofins')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors ml-auto"
                >
                  COFINS
                  <SortIcon field="cofins" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('totalTax')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors ml-auto"
                >
                  Total
                  <SortIcon field="totalTax" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => onSort('effectiveRate')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors ml-auto"
                >
                  Alíquota
                  <SortIcon field="effectiveRate" />
                </button>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-sm font-semibold text-foreground">Status</span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-sm font-semibold text-foreground">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {calculations.map((calc) => (
              <tr
                key={calc.id}
                className="hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => onProductClick(calc)}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedRows.has(calc.id)}
                    onChange={(e) => handleSelectRow(calc.id, e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                    aria-label={`Selecionar ${calc.productName}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{calc.productCode}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{calc.productName}</span>
                    {calc.exemptions.length > 0 && (
                      <span className="text-xs text-muted-foreground mt-1">
                        {calc.exemptions.length} isenção(ões)
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{calc.ncm}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-mono text-foreground">
                    {formatCurrency(calc.icms)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-mono text-foreground">
                    {formatCurrency(calc.ipi)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-mono text-foreground">
                    {formatCurrency(calc.pis)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-mono text-foreground">
                    {formatCurrency(calc.cofins)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-semibold font-mono text-foreground">
                    {formatCurrency(calc.totalTax)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-mono text-primary font-medium">
                    {calc.effectiveRate.toFixed(2)}%
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        calc.status
                      )}`}
                    >
                      {getStatusLabel(calc.status)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => onProductClick(calc)}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      aria-label="Ver detalhes"
                    >
                      <Icon name="Eye" size={16} color="var(--color-primary)" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {calculations.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhum resultado encontrado</p>
        </div>
      )}

      {selectedRows.size > 0 && (
        <div className="border-t border-border p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedRows.size} produto(s) selecionado(s)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                Exportar Selecionados
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedRows(new Set())}>
                Limpar Seleção
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsTable;